import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';

import UpdateBulkTerminateMutation from '@/graphql/contracts/updateBulkTerminate.mutation.gql';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { UpdateBulkTerminate, UpdateBulkTerminateVariables } from
  '@/graphql/contracts/_generated_/updateBulkTerminate.mutation';

import { notificationService } from '@/services/notification/notification.client';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import {
  triggerLineRefresh
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

type TerminateContractLineDataType = {
  contractLineID: UUID;
  terminationDate: Date;
  reasonCode: string;
  comment: string;
};

export type UpdateBulkTerminateDto = {
  contractId: UUID;
  terminateContractLines: TerminateContractLineDataType[];
};

export default function useUpdateBulkTerminate() {
  const { contractId }: { contractId: UUID } = useParams();

  const [_updateBulkTerminate, { loading }] = useMutation<UpdateBulkTerminate, UpdateBulkTerminateVariables>(
    UpdateBulkTerminateMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      awaitRefetchQueries: true,
      onCompleted: () => {
        triggerLineRefresh();
        triggerVariableRefresh();
        notificationService.open({
          title: 'Contract Line Status Saved',
          text: 'The chosen contract lines have been changed',
          type: 'success'
        });
      }
    }
  );

  const updateBulkTerminate = (input: UpdateBulkTerminateDto) => {
    _updateBulkTerminate({
      variables: input,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: { contractId }
        },
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        },
        {
          query: GetPaginatedFilteredVariableContractLinesQuery,
          variables: {
            filter: { headerPrimaryKey: { eq: contractId } },
            first: ALL_ITEMS,
          }
        }
      ]
    });
  };

  return {
    updateBulkTerminate,
    isLoadingUpdateBulkTerminate: loading,
  } as const;
}
